import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  LayoutDashboard, RefreshCw, Loader2, Wallet, HandCoins, TrendingUp, AlertTriangle,
  Boxes, CalendarClock, Eye, Percent, Trophy, Layers, Gauge, Clock, Phone, X,
  Coins, Users, FileSpreadsheet, FileText, ChevronDown, ChevronRight, History, CheckCircle2,
} from 'lucide-react';
import { fmtMoney, fmtK, FiltrosPrestamos, InformeButtons } from './_prestamosUtils';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../contexts/AuthContext';
import { SyncWayaStatus } from '../../components/prestamos/SyncWayaStatus';

const DATE_FIELDS = [
  { value: '', label: 'Sin filtro de fecha' },
  { value: 'solicitud', label: 'Fecha de solicitud' },
  { value: 'aprobacion', label: 'Fecha de aprob./rechazo' },
  { value: 'mora', label: 'Fecha de mora' },
  { value: 'vencimiento', label: 'Vencimiento de cuota' },
];

const estadoLoanColor = (e) => ({
  Mora: 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300',
  Vigente: 'bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300',
  Cancelado: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
  Autorizado: 'bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300',
  Rechazado: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
}[e] || 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300');

const cuotaBadge = (est) => {
  if (est === 'abonada') return <Badge className="bg-emerald-100 text-emerald-700 border-0">Abonada</Badge>;
  if (est === 'mora') return <Badge className="bg-rose-100 text-rose-700 border-0">Mora</Badge>;
  return <Badge className="bg-blue-100 text-blue-700 border-0">Pendiente</Badge>;
};

// Últimos `n` meses como opciones {value:'YYYY-MM', label:'mes YYYY'} (más reciente primero).
const mesesOpciones = (n = 15) => {
  const out = [];
  const hoy = new Date();
  for (let i = 0; i < n; i++) {
    const dt = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
    const value = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    let label = dt.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    label = label.charAt(0).toUpperCase() + label.slice(1);
    out.push({ value, label });
  }
  return out;
};
const selCls = 'h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm text-slate-700 dark:text-slate-200';

// ---- KPI card (bento) ----
const KpiCard = ({ icon: Icon, label, value, sub, tone = 'slate', testid }) => {
  const tones = {
    slate: 'text-slate-900 dark:text-slate-50', teal: 'text-teal-600 dark:text-teal-400',
    emerald: 'text-emerald-600 dark:text-emerald-400', rose: 'text-rose-600 dark:text-rose-400',
    sky: 'text-sky-600 dark:text-sky-400',
  };
  const ring = {
    slate: 'bg-slate-500/10 text-slate-500', teal: 'bg-teal-500/10 text-teal-600',
    emerald: 'bg-emerald-500/10 text-emerald-600', rose: 'bg-rose-500/10 text-rose-600',
    sky: 'bg-sky-500/10 text-sky-600',
  };
  return (
    <Card className="p-4 border-slate-200 dark:border-slate-800 transition-all duration-200 hover:shadow-md hover:border-teal-500/40" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`p-1.5 rounded-lg ${ring[tone]}`}><Icon className="h-4 w-4" /></span>
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <p className={`text-2xl font-bold tracking-tight ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
};

// ===================== CARTERA =====================
const CarteraView = ({ data, loading, onVer }) => {
  const items = data?.items || [];
  const a = data?.agregados || {};
  if (loading) return <div className="text-center text-slate-500 py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (items.length === 0) return (
    <div className="text-center text-slate-400 py-16" data-testid="cartera-empty">
      <Boxes className="h-10 w-10 mx-auto mb-3 opacity-40" />
      Sin préstamos para los filtros actuales. Si es la primera vez, usá "Actualizar desde Waya".
    </div>
  );
  return (
    <>
      {a.por_estado && Object.keys(a.por_estado).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="cartera-por-estado">
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{a.total_prestamos || 0} préstamo(s):</span>
          {Object.entries(a.por_estado).sort((x, y) => y[1] - x[1]).map(([est, cnt]) => (
            <Badge key={est} className={`${estadoLoanColor(est)} border-0`} data-testid={`cartera-estado-${est}`}>
              {est}: <span className="font-bold ml-1">{cnt}</span>
            </Badge>
          ))}
        </div>
      )}
      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5 font-medium">Cliente</th>
                <th className="text-center px-3 py-2.5 font-medium">Estado</th>
                <th className="text-left px-3 py-2.5 font-medium">Frec.</th>
                <th className="text-right px-3 py-2.5 font-medium">Prestado</th>
                <th className="text-right px-3 py-2.5 font-medium">Cobrado</th>
                <th className="text-right px-3 py-2.5 font-medium">Pendiente</th>
                <th className="text-right px-3 py-2.5 font-medium">Mora</th>
                <th className="text-center px-3 py-2.5 font-medium">Días</th>
                <th className="text-center px-3 py-2.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.prestamo_id} className={`border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${it.en_mora ? 'bg-rose-50/30 dark:bg-rose-950/10' : ''}`} data-testid={`cartera-row-${it.prestamo_id}`}>
                  <td className="px-3 py-2.5">
                    <p className="font-medium text-slate-800 dark:text-slate-100">{it.nombre || '—'}</p>
                    <p className="font-mono text-xs text-slate-500">{it.cuit}</p>
                  </td>
                  <td className="px-3 py-2.5 text-center"><Badge className={`${estadoLoanColor(it.estado)} border-0`}>{it.estado || '—'}</Badge></td>
                  <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{it.frecuencia || '—'}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-200">{fmtMoney(it.prestado)}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-600">{fmtMoney(it.cobrado)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-500">{it.pendiente_no_mora ? fmtMoney(it.pendiente_no_mora) : '—'}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-rose-600" data-testid={`cartera-mora-${it.prestamo_id}`}>{it.mora_consolidada ? fmtMoney(it.mora_consolidada) : '—'}</td>
                  <td className="px-3 py-2.5 text-center">{it.dias_mora > 0 ? <Badge className="bg-rose-100 text-rose-700 border-0">{it.dias_mora}</Badge> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2.5 text-center">
                    <Button variant="ghost" size="sm" className="h-8 text-teal-600" onClick={() => onVer(it.prestamo_id)} data-testid={`cartera-ver-${it.prestamo_id}`}><Eye className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
};

// ===================== MORA =====================
// Tabla de historial de mora (estuvo en mora / mora paga)
const TablaMoraHistorica = ({ items, prefix, mostrarRecupero }) => (
  <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
          <tr>
            <th className="text-left px-3 py-2.5 font-medium">Cliente</th>
            <th className="text-left px-3 py-2.5 font-medium">Teléfono</th>
            <th className="text-left px-3 py-2.5 font-medium">Frec.</th>
            <th className="text-left px-3 py-2.5 font-medium">Estado</th>
            <th className="text-right px-3 py-2.5 font-medium">Cap. prestado</th>
            <th className="text-right px-3 py-2.5 font-medium">Valor cuota</th>
            <th className="text-center px-3 py-2.5 font-medium">Cuotas (pag./tot.)</th>
            <th className="text-center px-3 py-2.5 font-medium">Pagadas tarde</th>
            <th className="text-center px-3 py-2.5 font-medium">Máx. atraso</th>
            {mostrarRecupero && <th className="text-right px-3 py-2.5 font-medium">Recupero</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.waya_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`${prefix}-row-${it.waya_id}`}>
              <td className="px-3 py-2.5"><p className="font-medium text-slate-800 dark:text-slate-100">{it.nombre_completo || '—'}</p><p className="font-mono text-xs text-slate-500">{it.cuit}</p></td>
              <td className="px-3 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300" data-testid={`${prefix}-tel-${it.waya_id}`}>{it.telefono || '—'}</td>
              <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{it.frecuencia || '—'}</td>
              <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{it.estado || '—'}</td>
              <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-200">{fmtMoney(it.monto_solicitado)}</td>
              <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-300">{fmtMoney(it.valor_cuota)}</td>
              <td className="px-3 py-2.5 text-center text-slate-600 dark:text-slate-300">{it.cuotas_pagadas}/{it.cuotas_totales}</td>
              <td className="px-3 py-2.5 text-center"><Badge className="bg-amber-100 text-amber-700 border-0">{it.cuotas_pagadas_tarde}</Badge></td>
              <td className="px-3 py-2.5 text-center">{it.max_atraso_dias > 0 ? <Badge className="bg-orange-100 text-orange-700 border-0" data-testid={`${prefix}-atraso-${it.waya_id}`}>{it.max_atraso_dias} d</Badge> : <span className="text-slate-400">—</span>}</td>
              {mostrarRecupero && <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-300">{fmtMoney(it.recupero_esperado)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

const MoraView = ({ data, loading }) => {
  const [sub, setSub] = React.useState('en-mora');
  const items = data?.items || [];
  const estuvo = data?.estuvo_en_mora || [];
  const paga = data?.mora_paga || [];
  const frec = data?.por_frecuencia || {};
  if (loading) return <div className="text-center text-slate-500 py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (!data) return <div className="text-center text-slate-400 py-16" data-testid="mora-sin-datos"><AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-40" />No se pudo cargar la mora.</div>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 border-rose-200 bg-rose-50/40 dark:bg-rose-950/10 dark:border-rose-900" data-testid="mora-total"><p className="text-xs text-slate-500">Mora total (activa)</p><p className="text-xl font-bold text-rose-600 mt-0.5">{fmtMoney(data.total_mora)}</p><p className="text-[11px] text-slate-400 mt-0.5">Capital {fmtK(data.total_capital)} + Punit. {fmtK(data.total_punitorios)}</p></Card>
        <Card className="p-4 border-slate-200 dark:border-slate-800" data-testid="mora-tasa"><p className="text-xs text-slate-500">Préstamos en mora</p><p className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">{data.prestamos_con_mora} / {data.total_prestamos}</p><p className="text-[11px] text-slate-400 mt-0.5">Tasa {data.tasa_mora_global}% · {data.prestamos_activos} activos</p></Card>
        <Card className="p-4 border-slate-200 dark:border-slate-800" data-testid="mora-recupero"><p className="text-xs text-slate-500">Recupero esperado</p><p className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">{fmtMoney(data.total_recupero_esperado)}</p><p className="text-[11px] text-slate-400 mt-0.5">Cuotas restantes × valor cuota</p></Card>
        <Card className="p-4 border-slate-200 dark:border-slate-800" data-testid="mora-proyeccion"><p className="text-xs text-slate-500">Por vencer 30/60/90</p><p className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">{fmtMoney(data.total_mora_futura_30d)}</p><p className="text-[11px] text-slate-400 mt-0.5">60d {fmtK(data.total_mora_futura_60d)} · 90d {fmtK(data.total_mora_futura_90d)}</p></Card>
      </div>
      {Object.keys(frec).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="mora-por-frecuencia">
          {Object.entries(frec).map(([f, s]) => (
            <Card key={f} className="p-3 border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between"><span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{f}</span><Badge className="bg-slate-100 text-slate-600 border-0">{s.count}</Badge></div>
              <p className="text-lg font-bold text-rose-600 mt-1">{fmtMoney(s.total_mora)}</p>
              <p className="text-[11px] text-slate-400">{s.prestamos_con_mora} en mora · {s.tasa_mora}%</p>
            </Card>
          ))}
        </div>
      )}

      <Tabs value={sub} onValueChange={setSub} className="w-full" data-testid="mora-subtabs">
        <TabsList className="mb-4" data-testid="mora-subtabs-list">
          <TabsTrigger value="en-mora" data-testid="mora-subtab-en-mora"><AlertTriangle className="h-4 w-4 mr-1.5 text-rose-500" />En mora <span className="ml-1.5 text-xs opacity-70">({data.count_en_mora})</span></TabsTrigger>
          <TabsTrigger value="estuvo" data-testid="mora-subtab-estuvo"><History className="h-4 w-4 mr-1.5 text-amber-500" />Estuvo en mora <span className="ml-1.5 text-xs opacity-70">({data.count_estuvo_en_mora})</span></TabsTrigger>
          <TabsTrigger value="paga" data-testid="mora-subtab-paga"><CheckCircle2 className="h-4 w-4 mr-1.5 text-emerald-500" />Mora paga <span className="ml-1.5 text-xs opacity-70">({data.count_mora_paga})</span></TabsTrigger>
        </TabsList>

        <TabsContent value="en-mora" data-testid="mora-content-en-mora">
          <p className="text-xs text-slate-500 mb-2">Préstamos con cuotas vencidas impagas al día de hoy.</p>
          {items.length === 0 ? (
            <div className="text-center text-slate-400 py-16" data-testid="mora-empty"><AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-40" />No hay préstamos en mora para los filtros actuales.</div>
          ) : (
            <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2.5 font-medium">Cliente</th>
                      <th className="text-left px-3 py-2.5 font-medium">Teléfono</th>
                      <th className="text-left px-3 py-2.5 font-medium">Frec.</th>
                      <th className="text-right px-3 py-2.5 font-medium">Cap. prestado</th>
                      <th className="text-right px-3 py-2.5 font-medium">Valor cuota</th>
                      <th className="text-center px-3 py-2.5 font-medium">Venc./Tot.</th>
                      <th className="text-center px-3 py-2.5 font-medium">Días mora</th>
                      <th className="text-right px-3 py-2.5 font-medium">Total mora</th>
                      <th className="text-right px-3 py-2.5 font-medium">→30d</th>
                      <th className="text-right px-3 py-2.5 font-medium">Recupero</th>
                      <th className="text-left px-3 py-2.5 font-medium">Próx. cuota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.waya_id} className="border-t border-slate-100 dark:border-slate-800 bg-rose-50/40 dark:bg-rose-950/10" data-testid={`mora-row-${it.waya_id}`}>
                        <td className="px-3 py-2.5"><p className="font-medium text-slate-800 dark:text-slate-100">{it.nombre_completo || '—'}</p><p className="font-mono text-xs text-slate-500">{it.cuit}</p></td>
                        <td className="px-3 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300" data-testid={`mora-tel-${it.waya_id}`}>{it.telefono || '—'}</td>
                        <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{it.frecuencia || '—'}</td>
                        <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-200">{fmtMoney(it.monto_solicitado)}</td>
                        <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-300">{fmtMoney(it.valor_cuota)}</td>
                        <td className="px-3 py-2.5 text-center text-slate-600 dark:text-slate-300">{it.cuotas_vencidas}/{it.cuotas_totales}</td>
                        <td className="px-3 py-2.5 text-center"><Badge className="bg-rose-100 text-rose-700 border-0">{it.dias_mora}</Badge></td>
                        <td className="px-3 py-2.5 text-right font-semibold text-rose-600" data-testid={`mora-total-${it.waya_id}`}>{fmtMoney(it.total_mora)}</td>
                        <td className="px-3 py-2.5 text-right text-slate-500">{it.monto_futuro_30d ? fmtMoney(it.monto_futuro_30d) : '—'}</td>
                        <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-300">{fmtMoney(it.recupero_esperado)}</td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{it.proxima_cuota_fvto || 'vencidas'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="estuvo" data-testid="mora-content-estuvo">
          <p className="text-xs text-slate-500 mb-2">Préstamos <strong>activos</strong> que hoy no están en mora, pero pagaron alguna cuota <strong>después</strong> del vencimiento (se regularizaron).</p>
          {estuvo.length === 0 ? (
            <div className="text-center text-slate-400 py-16" data-testid="estuvo-empty"><History className="h-10 w-10 mx-auto mb-3 opacity-40" />No hay préstamos activos que hayan estado en mora.</div>
          ) : <TablaMoraHistorica items={estuvo} prefix="estuvo" mostrarRecupero />}
        </TabsContent>

        <TabsContent value="paga" data-testid="mora-content-paga">
          <p className="text-xs text-slate-500 mb-2">Préstamos <strong>finalizados</strong> (sin cuotas pendientes) que en algún momento estuvieron en mora y terminaron de pagar.</p>
          {paga.length === 0 ? (
            <div className="text-center text-slate-400 py-16" data-testid="paga-empty"><CheckCircle2 className="h-10 w-10 mx-auto mb-3 opacity-40" />No hay préstamos finalizados que hayan estado en mora.</div>
          ) : <TablaMoraHistorica items={paga} prefix="paga" mostrarRecupero={false} />}
        </TabsContent>
      </Tabs>
    </>
  );
};

// ===================== RENTABILIDAD =====================
const RentabilidadView = ({ data, loading }) => {
  if (loading) return <div className="text-center text-slate-500 py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (!data) return <div className="text-center text-slate-400 py-16">Sin datos de rentabilidad.</div>;
  const u = data.universo || {}, m = data.mora || {}, p = data.proyeccion || { monto_futuro: {}, ganancia_proyectada: {} };
  const kr = data.kpis_rango || {};
  const rango = data.rango || {};
  const serie = data.serie_mensual || [];
  const maxSerie = Math.max(1, ...serie.map((s) => s.ingreso));
  const frec = Object.entries(data.por_frecuencia || {});
  return (
    <>
      <div className="mb-3 flex items-center gap-2 text-sm text-slate-500"><CalendarClock className="h-4 w-4" /> Período {rango.desde || '—'} → {rango.hasta || '—'}{rango.frecuencia ? ` · ${rango.frecuencia}` : ''}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="rent-rango">
        <KpiCard icon={HandCoins} label="Ingreso realizado (período)" value={fmtK(kr.ingreso_realizado)} tone="emerald" sub={fmtMoney(kr.ingreso_realizado)} testid="rent-rango-realizado" />
        <KpiCard icon={TrendingUp} label="Ingreso esperado (período)" value={fmtK(kr.ingreso_esperado)} sub={fmtMoney(kr.ingreso_esperado)} />
        <KpiCard icon={Percent} label="% Recupero (período)" value={`${kr.pct_recupero ?? 0}%`} tone="teal" />
      </div>
      <Card className="p-4 mb-6 border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between text-sm mb-2"><span className="text-slate-500">Recupero de la ganancia esperada</span><span className="font-semibold text-teal-600">{u.pct_recupero}%</span></div>
        <Progress value={Math.min(100, u.pct_recupero || 0)} className="h-3" />
      </Card>
      <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Proyección de cobro (acumulada)</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="rent-proyeccion">
        {['30', '60', '90'].map((k) => (
          <Card key={k} className="p-4 border-slate-200 dark:border-slate-800">
            <p className="text-xs uppercase tracking-wider text-slate-400">Próximos {k} días</p>
            <p className="text-xl font-bold text-slate-800 dark:text-slate-100 mt-1">{fmtMoney(p.monto_futuro[k])}</p>
            <p className="text-xs text-slate-500">por vencer</p>
            <p className="text-sm font-medium text-emerald-600 mt-2">+ {fmtMoney(p.ganancia_proyectada[k])}</p>
            <p className="text-xs text-slate-400">ganancia proyectada</p>
          </Card>
        ))}
      </div>
      <Card className="p-5 mb-6 border-slate-200 dark:border-slate-800">
        <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-4">Ingreso realizado — últimos 12 meses</h3>
        <div className="flex items-end gap-2 h-40" data-testid="rent-serie">
          {serie.map((s, i) => (
            <div key={i} className="flex-1 flex flex-col items-center justify-end group">
              <span className="text-[9px] text-slate-500 mb-1 opacity-0 group-hover:opacity-100 whitespace-nowrap">{fmtK(s.ingreso)}</span>
              <div className="w-full bg-teal-500/80 hover:bg-teal-500 rounded-t transition-colors" style={{ height: `${Math.max(2, (s.ingreso / maxSerie) * 100)}%` }} title={`${s.mes}: ${fmtMoney(s.ingreso)}`} />
              <span className="text-[9px] text-slate-400 mt-1">{s.mes.slice(2)}</span>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 border-slate-200 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2"><Trophy className="h-4 w-4" /> Top 10 por ganancia realizada</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rent-top10">
              <thead className="text-slate-400 text-xs"><tr><th className="text-left py-1.5 font-medium">Cliente</th><th className="text-right py-1.5 font-medium">Monto</th><th className="text-right py-1.5 font-medium">Realizada</th><th className="text-right py-1.5 font-medium">%</th><th className="text-right py-1.5 font-medium">Mora</th></tr></thead>
              <tbody>
                {(data.top10 || []).map((t, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-2 text-slate-700 dark:text-slate-200"><span className="block truncate max-w-[180px]">{t.nombre || t.cuit}</span><span className="text-[10px] text-slate-400">{t.frecuencia}</span></td>
                    <td className="py-2 text-right text-slate-600 dark:text-slate-300">{fmtK(t.monto_solicitado)}</td>
                    <td className="py-2 text-right text-emerald-600 font-medium">{fmtK(t.ingreso_realizado)}</td>
                    <td className="py-2 text-right text-slate-500">{t.pct_realizado}%</td>
                    <td className={`py-2 text-right ${t.total_mora > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{t.total_mora > 0 ? fmtK(t.total_mora) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card className="p-5 border-slate-200 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2"><Layers className="h-4 w-4" /> Por frecuencia</h3>
          <div className="space-y-3">
            {frec.map(([name, v]) => (
              <div key={name} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                <div className="flex items-center justify-between"><span className="font-medium text-slate-700 dark:text-slate-200">{name}</span><span className="text-xs text-slate-400">{v.count} préstamos</span></div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div><span className="text-slate-400">Realizada</span><p className="font-medium text-emerald-600">{fmtK(v.ingreso_realizado)}</p></div>
                  <div><span className="text-slate-400">Mora</span><p className="font-medium text-rose-600">{fmtK(v.total_mora)} · {v.tasa_mora}%</p></div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
};

// ===================== DRILL-DOWN 360° =====================
const DrillDown = ({ open, onClose, detalle, loading, decision }) => {
  const d = detalle;
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="panel-drilldown">
        <SheetHeader>
          <SheetTitle>Préstamo {d?.prestamo_id}</SheetTitle>
          <SheetDescription>Vista 360°: cuotas, pagos, mora, ganancia y evaluación del Motor.</SheetDescription>
        </SheetHeader>
        {loading || !d?.cuit ? (
          <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="space-y-5 mt-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{d.nombre}</p>
                <Badge className={`${estadoLoanColor(d.estado)} border-0`}>{d.estado || '—'}</Badge>
              </div>
              <p className="font-mono text-xs text-slate-500 flex items-center gap-2">{d.cuit}{d.telefono ? <><span>·</span><Phone className="h-3 w-3" />{d.telefono}</> : null}</p>
              <p className="text-xs text-slate-400 mt-0.5">{d.frecuencia} · {d.cantidad_cuotas} cuotas · Prestado {fmtMoney(d.monto_solicitado)}{d.tna ? ` · TNA ${d.tna}` : ''}</p>
              <p className="text-xs text-slate-400">Solicitado: {d.fecha_solicitud || '—'}{d.accion_realizada ? ` · ${d.accion_realizada}` : ''}{d.usuario_accion ? ` (${d.usuario_accion})` : ''}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Ingreso realizado</p><p className="font-bold text-emerald-600">{fmtMoney(d.ingreso_realizado)}</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Ganancia esperada</p><p className="font-semibold text-slate-700 dark:text-slate-200">{fmtMoney(d.ganancia_esperada)}</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">% Recupero</p><p className="font-semibold text-teal-600">{d.pct_realizado ?? 0}%</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Mora consolidada</p><p className="font-bold text-rose-600">{fmtMoney(d.total_mora)}</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Punitorios</p><p className="font-semibold text-slate-700 dark:text-slate-200">{fmtMoney(d.punitorios_saldo)}</p></div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Días mora</p><p className="font-semibold text-rose-600">{d.dias_mora || 0}</p></div>
            </div>
            {decision && (
              <div className="rounded-lg border border-teal-200 dark:border-teal-900 bg-teal-50/40 dark:bg-teal-950/10 p-3" data-testid="panel-drill-motor">
                <div className="flex items-center gap-2 mb-1"><Gauge className="h-4 w-4 text-teal-600" /><span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Última evaluación del Motor</span></div>
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <Badge className={`${decision.dictamen === 'Aprobar' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'} border-0`}>{decision.dictamen || '—'}</Badge>
                  {decision.score != null && <span className="text-slate-500">Score {decision.score}{decision.score_tier ? ` · ${decision.score_tier}` : ''}</span>}
                  {decision.evaluado_at && <span className="text-xs text-slate-400">{String(decision.evaluado_at).slice(0, 10)}</span>}
                </div>
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">Cuotas</p>
              <div className="space-y-2" data-testid="panel-drill-cuotas">
                {(d.cuotas || []).map((c, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-slate-700 dark:text-slate-200">{c.cuota || c.nro}</span>
                        {c.es_punitorio && <Badge className="ml-2 bg-purple-100 text-purple-700 border-0">Punitorios</Badge>}
                        <span className="ml-2 text-xs text-slate-400">vto {c.fecha_vto}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">Cuota {fmtMoney(c.total)}</span>
                        <span className={`text-sm font-semibold ${c.estado === 'mora' ? 'text-rose-600' : c.estado === 'pendiente' ? 'text-blue-600' : 'text-emerald-600'}`}>Saldo {fmtMoney(c.saldo)}</span>
                        {cuotaBadge(c.estado)}
                      </div>
                    </div>
                    {(c.pagos || []).length > 0 && (
                      <div className="mt-2 pl-3 border-l-2 border-slate-100 dark:border-slate-700 space-y-0.5">
                        {c.pagos.map((p, j) => (
                          <div key={j} className="flex justify-between text-xs text-slate-500"><span>Pago {p.fecha}</span><span className="text-emerald-600">{fmtMoney(p.monto)}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ===================== COBRANZAS DEL MES =====================
const CobranzasView = ({ defaultMes }) => {
  const hoyISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const MESES = mesesOpciones(15);
  const [mes, setMes] = useState(defaultMes || MESES[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exp, setExp] = useState({});
  const [downloading, setDownloading] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.rol === 'ADMINISTRADOR';
  const [sel, setSel] = useState({});
  const [marcas, setMarcas] = useState({});
  const [facturando, setFacturando] = useState(false);
  const [puedeFacturar, setPuedeFacturar] = useState(false);
  const [fechaEmision, setFechaEmision] = useState(hoyISO());

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const r = await api.get(`/kpis/prestamos/composicion/${m}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar la cobranza del mes');
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(mes); }, [mes, load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/arca/facturacion/puede-facturar-cobranzas');
        setPuedeFacturar(!!r.data?.puede);
      } catch (e) { setPuedeFacturar(false); }
    })();
  }, []);

  useEffect(() => {
    setSel({});
    (async () => {
      try {
        const r = await api.get('/arca/facturacion/lotes-marcas', { params: { periodo: mes } });
        const m = {}; (r.data || []).forEach((x) => { if (x.prestamo_id) m[x.prestamo_id] = x; });
        setMarcas(m);
      } catch (e) { setMarcas({}); }
    })();
  }, [mes]);

  const descargar = async (formato) => {
    setDownloading(true);
    try {
      toast.info(`Generando ${formato === 'pdf' ? 'PDF' : 'Excel'}…`);
      const r = await api.get(`/kpis/prestamos/composicion/${mes}/export`, { params: { formato }, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cobranzas_${mes}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo generar el archivo');
    } finally { setDownloading(false); }
  };

  const items = data?.items || [];
  const mesLabel = MESES.find((m) => m.value === mes)?.label || mes;
  const MESNOM = ['', 'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
  const mesLabelUpper = (() => { const p = String(mes).split('-'); return `${MESNOM[Number(p[1])] || ''} ${p[0]}`.trim(); })();
  const selectable = (it) => (it.ganancia_mes || 0) > 0 && (String(it.cuit || '').replace(/\D/g, '').length === 11);
  const yaFacturado = (it) => !!marcas[it.prestamo_id];
  // Admin puede re-facturar lo ya facturado; el resto no.
  const facturables = items.filter((it) => selectable(it) && (isAdmin || !yaFacturado(it)));
  const selCount = Object.keys(sel).filter((k) => sel[k]).length;
  const allSelected = facturables.length > 0 && facturables.every((it) => sel[it.prestamo_id]);
  const toggleAll = () => {
    const n = { ...sel };
    facturables.forEach((it) => { n[it.prestamo_id] = !allSelected; });
    setSel(n);
  };
  const facturar = async () => {
    const elegidos = items.filter((it) => sel[it.prestamo_id] && selectable(it) && (isAdmin || !yaFacturado(it)));
    if (elegidos.length === 0) { toast.error('No hay clientes válidos seleccionados (CUIT de 11 dígitos, ganancia > 0 y no facturados)'); return; }
    setFacturando(true);
    try {
      const payload = {
        origen: 'prestamos-cobranzas', periodo: mes, mes_label: mesLabelUpper, descripcion_base: 'CUOTA PRESTAMO WAYA',
        cbte_fch: (fechaEmision || '').replace(/-/g, ''),
        items: elegidos.map((it) => ({ prestamo_id: it.prestamo_id, cliente: it.nombre, cuit: it.cuit, monto: it.ganancia_mes })),
      };
      const r = await api.post('/arca/facturacion/lotes', payload);
      toast.success(`Lote creado con ${r.data.total} comprobante(s). Andá a ARCA → Facturación → Lotes para ejecutarlo.`);
      setSel({});
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo crear el lote'); } finally { setFacturando(false); }
  };

  return (
    <div data-testid="cobranzas-view">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40">
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-slate-500">Mes de cobranza</label>
            <select className={selCls} value={mes} onChange={(e) => setMes(e.target.value)} data-testid="cobranzas-mes">
              {MESES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <p className="text-xs text-slate-400 pb-1.5">Detalle de quiénes pagaron en <span className="font-medium text-slate-600 dark:text-slate-300">{mesLabel}</span></p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={() => descargar('excel')} disabled={downloading} data-testid="cobranzas-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1 text-emerald-600" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => descargar('pdf')} disabled={downloading} data-testid="cobranzas-export-pdf"><FileText className="h-4 w-4 mr-1 text-rose-600" />PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <KpiCard icon={Coins} label="Total cobrado (mes)" value={fmtK(data?.total_pagado)} sub={fmtMoney(data?.total_pagado)} tone="emerald" testid="cobranzas-total-pagado" />
        <KpiCard icon={TrendingUp} label="Ganancia del mes" value={fmtK(data?.total_ganancia)} sub={fmtMoney(data?.total_ganancia)} tone="teal" testid="cobranzas-total-ganancia" />
        <KpiCard icon={Users} label="Personas que pagaron" value={data?.cantidad_personas || 0} sub={`en ${mesLabel}`} tone="sky" testid="cobranzas-personas" />
      </div>

      {puedeFacturar && selCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900" data-testid="cobranzas-facturar-bar">
          <span className="text-sm text-emerald-800 dark:text-emerald-300 font-medium">{selCount} cliente(s) seleccionado(s) · se facturará la Ganancia de cada uno</span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-emerald-800 dark:text-emerald-300 font-medium whitespace-nowrap">Fecha factura</label>
            <input
              type="date"
              value={fechaEmision}
              onChange={(e) => setFechaEmision(e.target.value)}
              className="h-9 rounded-md border border-emerald-300 bg-white dark:bg-slate-900 px-2 text-sm text-slate-700 dark:text-slate-200"
              data-testid="cobranzas-fecha-emision"
            />
            <Button size="sm" onClick={facturar} disabled={facturando} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cobranzas-facturar-btn">
              {facturando ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <HandCoins className="h-4 w-4 mr-1" />} Facturar ({selCount})
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-500 py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center text-slate-400 py-16" data-testid="cobranzas-empty">
          <Coins className="h-10 w-10 mx-auto mb-3 opacity-40" />
          No se registraron pagos en {mesLabel}. Probá con otro mes.
        </div>
      ) : (
        <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
                <tr>
                  {puedeFacturar && <th className="px-3 py-2.5 w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="cobranzas-check-all" /></th>}
                  <th className="text-left px-3 py-2.5 font-medium w-8"></th>
                  <th className="text-left px-3 py-2.5 font-medium">Cliente</th>
                  <th className="text-left px-3 py-2.5 font-medium">Frec.</th>
                  <th className="text-center px-3 py-2.5 font-medium">Estado</th>
                  <th className="text-center px-3 py-2.5 font-medium">Pagos</th>
                  <th className="text-right px-3 py-2.5 font-medium">Pagó en el mes</th>
                  <th className="text-right px-3 py-2.5 font-medium">Ganancia</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const isOpen = !!exp[it.prestamo_id];
                  return (
                    <React.Fragment key={it.prestamo_id}>
                      <tr className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer" onClick={() => setExp((p) => ({ ...p, [it.prestamo_id]: !p[it.prestamo_id] }))} data-testid={`cobranzas-row-${it.prestamo_id}`}>
                        {puedeFacturar && (
                          <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={!!sel[it.prestamo_id]} disabled={!selectable(it) || (yaFacturado(it) && !isAdmin)} onCheckedChange={(v) => setSel((p) => ({ ...p, [it.prestamo_id]: !!v }))} data-testid={`cobranzas-check-${it.prestamo_id}`} />
                          </td>
                        )}
                        <td className="px-3 py-2.5 text-slate-400">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div><p className="font-medium text-slate-800 dark:text-slate-100">{it.nombre || '—'}</p><p className="font-mono text-xs text-slate-500">{it.cuit}</p></div>
                            {marcas[it.prestamo_id] && <Badge className="bg-emerald-100 text-emerald-700 border-0" data-testid={`cobranzas-facturado-${it.prestamo_id}`}>Facturado</Badge>}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{it.frecuencia || '—'}</td>
                        <td className="px-3 py-2.5 text-center"><Badge className={`${estadoLoanColor(it.estado)} border-0`}>{it.estado || '—'}</Badge></td>
                        <td className="px-3 py-2.5 text-center text-slate-500">{(it.pagos || []).length}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-emerald-600" data-testid={`cobranzas-pagado-${it.prestamo_id}`}>{fmtMoney(it.pagado_mes)}</td>
                        <td className="px-3 py-2.5 text-right text-teal-600">{fmtMoney(it.ganancia_mes)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/60 dark:bg-slate-800/30" data-testid={`cobranzas-detalle-${it.prestamo_id}`}>
                          <td colSpan={puedeFacturar ? 8 : 7} className="px-3 py-2">
                            <div className="space-y-1">
                              {(it.pagos || []).map((p, j) => (
                                <div key={j} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 dark:border-slate-800 last:border-0">
                                  <span className="text-slate-500">
                                    {p.es_punitorio ? <Badge className="bg-purple-100 text-purple-700 border-0 mr-2">Punitorios</Badge> : <span className="text-slate-400 mr-2">Cuota {p.cuota}</span>}
                                    {p.imputado ? <span className="italic text-slate-400">(vto {p.fecha}, sin pago registrado)</span> : `Pago ${p.fecha}`}
                                  </span>
                                  <span className="flex items-center gap-4">
                                    {!p.imputado && <span className="text-emerald-600">{fmtMoney(p.monto)}</span>}
                                    <span className="text-teal-600 w-24 text-right">Gcia {fmtMoney(p.ganancia)}</span>
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

// ===================== PANEL =====================
const PanelPrestamos = () => {
  const [filtros, setFiltros] = useState({ date_field: '', desde: '', hasta: '', estado: '', frecuencia: '', q: '' });
  const [tab, setTab] = useState('cartera');
  const [inv, setInv] = useState(null);
  const [mora, setMora] = useState(null);
  const [rent, setRent] = useState(null);
  const [loadingInv, setLoadingInv] = useState(true);
  const [loadingMora, setLoadingMora] = useState(true);
  const [loadingRent, setLoadingRent] = useState(true);
  const [recalc, setRecalc] = useState(false);
  const [syncKey, setSyncKey] = useState(0);
  const [detalle, setDetalle] = useState(null);
  const [detOpen, setDetOpen] = useState(false);
  const [detLoading, setDetLoading] = useState(false);
  const [detDecision, setDetDecision] = useState(null);
  const pollRef = useRef(null);

  const loadAll = useCallback(async (f) => {
    const p = f || filtros;
    setLoadingInv(true); setLoadingMora(true); setLoadingRent(true);
    api.get('/motor/inventario', { params: p }).then((r) => setInv(r.data)).catch((e) => { toast.error(e?.response?.data?.detail || 'No se pudo cargar la cartera'); }).finally(() => setLoadingInv(false));
    api.get('/motor/mora', { params: p }).then((r) => setMora(r.data)).catch(() => {}).finally(() => setLoadingMora(false));
    api.get('/motor/rentabilidad', { params: { desde: p.desde, hasta: p.hasta, frecuencia: p.frecuencia } }).then((r) => setRent(r.data)).catch(() => {}).finally(() => setLoadingRent(false));
  }, [filtros]);

  useEffect(() => {
    loadAll(filtros);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line

  const abrirDetalle = async (id) => {
    setDetOpen(true); setDetLoading(true); setDetalle({ prestamo_id: id }); setDetDecision(null);
    try {
      const r = await api.get(`/motor/inventario/${id}`);
      setDetalle(r.data);
      if (r.data?.cuit) {
        api.get('/motor/decisiones', { params: { cuit: r.data.cuit.replace(/[^0-9]/g, ''), limit: 1 } })
          .then((dr) => { const it = (dr.data?.decisiones || dr.data?.items || [])[0]; if (it) setDetDecision(it); })
          .catch(() => {});
      }
    } catch (e) {
      toast.error('No se pudo cargar el detalle del préstamo'); setDetOpen(false); setDetalle(null);
    } finally { setDetLoading(false); }
  };

  const actualizarWaya = async () => {
    setRecalc(true);
    try {
      await api.post('/kpis/automatizaciones/prestamos-detalle-mora/ejecutar');
      toast.info('Actualizando: bajando el detalle de cuotas y pagos desde Waya…');
      setTimeout(() => setSyncKey((k) => k + 1), 1500);
      let intentos = 0;
      pollRef.current = setInterval(async () => {
        intentos += 1;
        if (intentos > 80) { clearInterval(pollRef.current); pollRef.current = null; setRecalc(false); setSyncKey((k) => k + 1); toast.error('Está tardando demasiado. Reintentá en unos minutos.'); loadAll(filtros); return; }
        try {
          const r = await api.get('/kpis/prestamos/detalle');
          const st = r.data?.scrape?.status;
          if (st && st !== 'en_ejecucion') {
            clearInterval(pollRef.current); pollRef.current = null; setRecalc(false); setSyncKey((k) => k + 1);
            if (st === 'error') toast.error('Falló la sincronización con Waya'); else toast.success('Datos actualizados desde Waya');
            loadAll(filtros);
          }
        } catch (e) { /* seguir */ }
      }, 6000);
    } catch (e) {
      setRecalc(false);
      if (e?.response?.status === 409) { toast.info(e.response.data.detail); setSyncKey((k) => k + 1); return; }
      toast.error(e?.response?.data?.detail || 'No se pudo iniciar la actualización');
    }
  };

  const a = inv?.agregados || {};
  const u = rent?.universo || {};
  const proy = rent?.proyeccion?.monto_futuro || {};
  const vigentes = a.por_estado?.Vigente || 0;
  // Mes por defecto para Cobranzas: el más reciente con ingreso realizado > 0.
  const serieR = rent?.serie_mensual || [];
  let defaultCobMes;
  for (let i = serieR.length - 1; i >= 0; i--) { if ((serieR[i].ingreso || 0) > 0) { defaultCobMes = serieR[i].mes; break; } }

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-7xl mx-auto" data-testid="panel-prestamos">
      {/* Header */}
      <div className="mb-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-500/10"><LayoutDashboard className="h-6 w-6 text-teal-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white" data-testid="panel-title">Panel de Préstamos</h1>
            <p className="text-sm text-slate-500">Cartera, mora y rentabilidad en un solo lugar{inv?.hoy ? ` · al ${inv.hoy}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {tab !== 'cobranzas' && <InformeButtons tipo={tab === 'mora' ? 'mora' : tab === 'rentabilidad' ? 'rentabilidad' : 'inventario'} params={filtros} />}
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white active:scale-95 transition-transform" onClick={actualizarWaya} disabled={recalc} data-testid="panel-sync-btn">
            {recalc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {recalc ? 'Actualizando…' : 'Actualizar desde Waya'}
          </Button>
        </div>
      </div>

      <SyncWayaStatus refreshKey={syncKey} />

      {/* KPI bento */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <KpiCard icon={Wallet} label="Capital colocado" value={fmtK(a.total_prestado)} sub={`${a.total_prestamos || 0} préstamos`} tone="slate" testid="kpi-capital-colocado" />
        <KpiCard icon={HandCoins} label="Cobrado" value={fmtK(a.total_cobrado)} tone="emerald" testid="kpi-total-cobrado" />
        <KpiCard icon={TrendingUp} label="Ganancia realizada" value={fmtK(u.ganancia_realizada)} sub={`${u.pct_recupero ?? 0}% recupero`} tone="teal" testid="kpi-ganancia-realizada" />
        <KpiCard icon={AlertTriangle} label="Mora consolidada" value={fmtK(a.total_mora)} sub={`Tasa ${a.tasa_mora || 0}%`} tone="rose" testid="kpi-mora-consolidada" />
        <KpiCard icon={Boxes} label="Préstamos activos" value={a.total_prestamos || 0} sub={`${vigentes} vigentes · ${a.prestamos_con_mora || 0} en mora`} tone="slate" testid="kpi-prestamos-activos" />
        <KpiCard icon={Clock} label="Proyección 30 días" value={fmtK(proy['30'])} sub="Cobros esperados" tone="sky" testid="kpi-proyeccion-cobro" />
      </div>

      {/* Filtros compartidos (no aplican a Cobranzas, que usa su propio selector de mes) */}
      {tab !== 'cobranzas' && (
        <FiltrosPrestamos value={filtros} onChange={setFiltros} onApply={(f) => loadAll(f)} dateFields={DATE_FIELDS} />
      )}

      {/* Lentes */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4" data-testid="panel-tabs">
          <TabsTrigger value="cartera" data-testid="tab-cartera" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white"><Boxes className="h-4 w-4 mr-1.5" />Cartera</TabsTrigger>
          <TabsTrigger value="mora" data-testid="tab-mora" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white"><AlertTriangle className="h-4 w-4 mr-1.5" />Mora</TabsTrigger>
          <TabsTrigger value="cobranzas" data-testid="tab-cobranzas" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white"><Coins className="h-4 w-4 mr-1.5" />Cobranzas</TabsTrigger>
          <TabsTrigger value="rentabilidad" data-testid="tab-rentabilidad" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white"><TrendingUp className="h-4 w-4 mr-1.5" />Rentabilidad</TabsTrigger>
        </TabsList>
        <TabsContent value="cartera"><CarteraView data={inv} loading={loadingInv} onVer={abrirDetalle} /></TabsContent>
        <TabsContent value="mora"><MoraView data={mora} loading={loadingMora} /></TabsContent>
        <TabsContent value="cobranzas"><CobranzasView defaultMes={defaultCobMes} /></TabsContent>
        <TabsContent value="rentabilidad"><RentabilidadView data={rent} loading={loadingRent} /></TabsContent>
      </Tabs>

      <DrillDown open={detOpen} onClose={() => { setDetOpen(false); setDetalle(null); }} detalle={detalle} loading={detLoading} decision={detDecision} />
    </div>
  );
};

export default PanelPrestamos;
